import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../core/AuthContext'
import { reportApi } from '../lib/api'
import { pages } from '../lib/metadata'
import PageHead from '../components/PageHead'
import Icon from '../components/Icon'
import Badge from '../components/Badge'
import { formatRelativeTime } from '../lib/utils'

function greeting() {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

export default function HomePage() {
  const { user } = useAuth()
  const firstName = user?.name?.split(' ')[0] || 'there'

  const { data: reports = [], error: reportsError } = useQuery({
    queryKey: ['reports-list'],
    queryFn: () => reportApi.list().then((res) => res.data),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })

  const sent = reports.length
  const resolved = reports.filter((r) => String(r.status).toUpperCase() === 'RESOLVED').length
  const active = reports.filter((r) => String(r.status).toUpperCase() !== 'RESOLVED').length
  const recent = reports.slice(0, 2)

  return (
    <>
      <PageHead title={pages.home.title} description={pages.home.description} canonical="https://snagmatic.com/home" />
      <div className="px-4 md:px-10 py-6 space-y-8">
      {/* Greeting */}
      <section className="space-y-1">
        <h1 className="text-headline-lg-mobile md:text-headline-lg">
          {greeting()}, {firstName}
        </h1>
        <p className="text-body-md text-text-secondary">
          Improving our city together, one report at a time.
        </p>
      </section>

      {/* Primary CTA */}
      <section className="relative overflow-hidden rounded-xl bg-surface-high border border-border p-6 md:p-8">
        <div className="relative z-10 flex flex-col items-start gap-4">
          <div className="bg-accent/15 border border-accent/20 p-3 rounded-xl">
            <Icon name="campaign" className="text-accent text-4xl" />
          </div>
          <div className="space-y-1 max-w-md">
            <h2 className="text-headline-md">Spot something that needs fixing?</h2>
            <p className="text-body-md text-text-secondary">
              Report potholes, broken streetlights, or graffiti directly to the local authorities.
            </p>
          </div>
          <Link
            to="/report/new"
            className="mt-2 inline-flex items-center gap-2 bg-accent text-surface-dark font-semibold text-label-md px-6 py-3.5 rounded-xl shadow-lg shadow-accent/20 hover:bg-accent-light active:scale-95 transition"
          >
            <Icon name="add_circle" className="text-xl" />
            Report an issue
          </Link>
        </div>
        <div className="absolute -right-16 -bottom-16 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
      </section>

      {/* My reports preview */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-headline-md">My reports</h2>
          <Link to="/reports" className="text-accent text-label-md hover:underline">
            View all
          </Link>
        </div>

        {reportsError ? (
          <div className="bg-status-urgent/10 border border-status-urgent rounded-xl p-8 text-center">
            <p className="text-body-md text-status-urgent">
              Could not load reports. Please check your connection and try again.
            </p>
          </div>
        ) : recent.length === 0 ? (
          <div className="bg-surface border border-border rounded-xl p-8 text-center">
            <p className="text-body-md text-text-secondary">
              No reports yet. Tap "Report an issue" to submit your first one.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recent.map((report) => (
              <Link
                key={report.id}
                to={`/reports/${report.id}`}
                className="bg-surface border border-border rounded-xl overflow-hidden flex flex-col hover:border-accent/50 transition-colors group"
              >
                {report.photo_thumb_path && (
                  <div className="relative h-44 w-full overflow-hidden bg-surface-high">
                    <img
                      src={report.photo_thumb_path}
                      alt="Report"
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3">
                      <Badge status={report.status} />
                    </div>
                  </div>
                )}
                <div className="p-4 flex-1 space-y-1">
                  <div className="flex items-center gap-1.5 text-text-muted text-label-sm">
                    <Icon name="location_on" className="text-sm" />
                    {report.location_text || 'Location pending'}
                  </div>
                  <h3 className="text-label-md text-text-primary">{report.category}</h3>
                  <div className="pt-1 flex items-center gap-1.5 text-text-muted text-label-sm">
                    <Icon name="schedule" className="text-sm" />
                    {formatRelativeTime(report.created_at)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard value={sent} label="Reports Sent" color="text-accent" />
        <StatCard value={resolved} label="Resolved" color="text-status-resolved" />
        <StatCard value={active} label="Active" color="text-status-pending" />
        <StatCard value={sent > 0 ? '4.8' : '—'} label="Citizen Score" color="text-text-primary" />
      </section>
      </div>
    </>
  )
}

function StatCard({ value, label, color }) {
  return (
    <div className="bg-surface-low border border-border rounded-xl p-4 text-center">
      <p className={`text-headline-md ${color}`}>{value}</p>
      <p className="text-label-sm text-text-muted">{label}</p>
    </div>
  )
}
